import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Microscope, Clock, MapPin, UserCheck } from "lucide-react";
import LoadingCircle from "../../components/Common/LoadingCircle";
import LandingPageHeader from "../../components/LandingPage/LandingPageHeader";
import { getDataByAll } from "../../api/scientificApi"; 

const ScientificProgram = ({ bgColor = "bg-white" }) => {
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDataByAll().then((data) => {
      setDatas(data);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <section className="py-20 flex flex-col items-center justify-center min-h-[400px]">
       <LoadingCircle size="lg" />
        <p className="mt-4 text-slate-400 font-mono text-[10px] uppercase tracking-widest animate-pulse">
          Retrieving Data...
        </p>
      </section>
    );
  }

  return (
    <section id="scientific-program" className={`pt-4 md:pt-8 pb-16 scroll-mt-24 lg:scroll-mt-44 transition-colors ${bgColor}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">     
        {/* Header Section */}
        <div className="-mt-2 md:-mt-4 mb-12">
          <LandingPageHeader 
            icon={Microscope} 
            title="Scientific Program" 
            subtitle="Explore the latest research and session schedules" 
            center={false} 
          />
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {datas.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-slate-900/60 p-6 rounded-[1.5rem] border border-slate-100 dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-300 group relative overflow-hidden"
            >
              {/* Decorative Accent */}
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-600 group-hover:w-2 transition-all" />

              <div className="flex flex-col h-full">
                {/* Time & Session Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                      <Clock size={14} />
                    </div>
                    <span className="text-[10px] font-black font-mono text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                      {item.time}
                    </span>
                  </div>
                  <span className="text-[9px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded-full border border-blue-100 dark:border-blue-800">
                    {item.session}
                  </span>
                </div>

                {/* Topic & Speakers */}
                <div className="flex-grow">
                  <h4 className="text-lg font-black text-slate-900 dark:text-white leading-tight group-hover:text-blue-600 transition-colors">
                    {item.topic}
                  </h4>
                  
                  <div className="mt-3 space-y-2">
                    {/* Hall Location */}
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                      <MapPin size={12} className="text-blue-500" />
                      <p className="text-[11px] font-bold uppercase tracking-tight">{item.hall}</p>
                    </div>

                    {/* Speakers List */}
                    <div className="flex items-start gap-2 text-slate-500 dark:text-slate-400">
                      <UserCheck size={12} className="text-blue-500 mt-0.5" />
                      <p className="text-[11px] leading-relaxed">
                        <span className="font-bold">Speaker:</span> {item.speakers.join(", ")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tags / Categories */}
                <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-slate-50 dark:border-white/5">
                  {item.tags.map((tag, i) => (
                    <span key={i} className="px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 text-[8px] font-bold rounded uppercase tracking-tighter">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ScientificProgram;